import { IGoodgameBitmapClip } from "../clip/IGoodgameBitmapClip";

import Event = createjs.Event;
import MouseEvent = createjs.MouseEvent;
import Point = createjs.Point;

export class InteractiveBitmapClipComponent {
    /** alpha threshold */
    private _threshold: number = 0;

    /**
     * Set to 0 to detect hit on any pixel that is not completely transparent, or 255
     * detect only completely opaque pixels.
     *
     * @default    0
     * @return    An alpha threshold between 0 and 255.
     */
    public get alphaTolerance(): number {
        return this._threshold;
    }

    public set alphaTolerance(value: number) {
        this._threshold = Math.min(255, value);
    }

    /** indicates if the mouse is currently on a clear Spot on the Bitmap */
    private _transparentMode: boolean = false;

    /** the last BitmapHitTest result */
    private _bitmapHit: boolean = false;

    /** the last Mouse position on the Bitmap. */
    private _mousePoint: Point = new Point();

    /** basePoint for BitmapHitTest. This prevents that a new Object has to be created every Updatecycle */
    private static BASE_POINT: Point = new Point();

    /** stores the nextUpdateTimestamp for mouse Updates */
    private _nextUpdateTimestamp: number;

    /** Update Intervall for Mouse Updates */
    private MOUSE_UPDATE_INTERVAL: number = Number(1000 / 24);

    /** owner of this component */
    private componentOwner: IGoodgameBitmapClip;

    constructor(src: IGoodgameBitmapClip) {
        this.componentOwner = src;
        this.activateMouseTrap();
    }

    /**
     * Listens for hit to edges of this MovieClip, which then turns on bitmap hit tracking.
     */
    private activateMouseTrap(): void {
        this.componentOwner.addEventListener("mouseover", this.bindFunction(this.captureMouseEvent));
        this.componentOwner.addEventListener("mouseout", this.bindFunction(this.captureMouseEvent));
        this.componentOwner.addEventListener("rollover", this.bindFunction(this.captureMouseEvent));
        this.componentOwner.addEventListener("rollout", this.bindFunction(this.captureMouseEvent));
        this.componentOwner.addEventListener("mousemove", this.bindFunction(this.captureMouseEvent));
    }

    /**
     * Turns off listening for mouse events on this MovieClip.         *
     */
    private deactivateMouseTrap(): void {
        this.componentOwner.removeEventListener("mouseover", this.bindFunction(this.captureMouseEvent));
        this.componentOwner.removeEventListener("mouseout", this.bindFunction(this.captureMouseEvent));
        this.componentOwner.removeEventListener("rollover", this.bindFunction(this.captureMouseEvent));
        this.componentOwner.removeEventListener("rollout", this.bindFunction(this.captureMouseEvent));
        this.componentOwner.removeEventListener("mousemove", this.bindFunction(this.captureMouseEvent));
    }

    /**
     * Captures and suppresses MouseEvents as the mouse enters this MovieClip's bounds.
     * Mouse is then tracked onEnterFrame by trackMouseWhileInBounds() until it leaves
     * clip bounds again. If the mouse hits a pixel this method is unsubscribed until
     * it falls off the bitmap again, otherwise it is left on to suppress all events.
     *
     * @param event        Any event subscribed in activateMouseTrap().
     */
    private captureMouseEvent(event: Event): void {
        if (!this._transparentMode) {
            if (event.type == "mouseover" || event.type == "rollover") {
                this._transparentMode = true;
                this.componentOwner.mouseEnabled = false;
                this.componentOwner.addEventListener(Event.ENTER_FRAME, this.bindFunction(this.trackMouseWhileInBounds), false); // activates bitmap hit & exit tracking
                this.trackMouseWhileInBounds(); // important: Immediate response, and sets _bitmapHit to correct state for event suppression.
            }
        }

        if (!this._bitmapHit) {
            event.stopImmediatePropagation();
        }
    }

    /**
     * Actively track the mouse for bitmap hit or exit of MovieClip bounds.
     * @param event        This method is called on ENTER_FRAME when mouse is in bounds and mouseEnabled is turned off.
     */
    private trackMouseWhileInBounds(event: Event = null): void {
        let now: number = Date.now();

        if (now > this._nextUpdateTimestamp) {
            if (this.bitmapHitTest() != this._bitmapHit) {
                this._bitmapHit = !this._bitmapHit;

                // Mouse is now on a nonclear pixel based on alphaTolerance. Reenable mouse events.
                if (this._bitmapHit) {
                    this.deactivateMouseTrap();
                    this._transparentMode = false;
                    // This will trigger rollOver & mouseOver events
                    this.componentOwner.mouseEnabled = true;
                }
                // Mouse is now on a clear pixel based on alphaTolerance. Disable mouse events.
                else if (!this._bitmapHit) {
                    this._transparentMode = true;
                    // This will trigger rollOut & mouseOut events
                    this.componentOwner.mouseEnabled = false;
                }
            }

            let localMouse: Point = this.componentOwner.bmpImage.localToGlobal(this._mousePoint.x, this._mousePoint.y);

            if (!this.componentOwner.hitTestPoint(localMouse.x, localMouse.y, true)) {
                this.componentOwner.removeEventListener(Event.ENTER_FRAME, this.bindFunction(this.trackMouseWhileInBounds));
                this._transparentMode = false;
                this.componentOwner.mouseEnabled = true;
                this.activateMouseTrap();
            }
            this._nextUpdateTimestamp = now + this.MOUSE_UPDATE_INTERVAL;
        }
    }

    /**
     * @return    Whether the mouse touches a pixel with an alpha value greater than the
     *             <code>alphaTolerance</code> setting.
     */
    private bitmapHitTest(): boolean {
        // TODO: @HTML5
        /*this._mousePoint.x = this.componentOwner.bmpImage.mouseX;
        this._mousePoint.y = this.componentOwner.bmpImage.mouseY;
        return this.componentOwner.bmpImage.bitmapData.hitTest(InteractiveBitmapClipComponent.BASE_POINT, this._threshold, this._mousePoint);*/
        return false;
    }

    /**
     * make object ready for garbage collector
     */
    public dispose(): void {
        this.deactivateMouseTrap();
    }
}
